import React, { useEffect, useId, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { AdsTableActionButton } from './AdsTable';

/**
 * Table row actions — 1–2 primary buttons + ⋯ overflow when crowded.
 * Reuses AdsTableActionButton / .et-action-btn; no parallel menu system.
 *
 * actions: [{ key, label, icon, onClick, variant?, danger? }]
 */
export default function AdsRowActions({
  actions = [],
  maxInline = 2,
  className = '',
  moreLabel = 'More actions',
}) {
  const visible = (actions || []).filter(Boolean);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!visible.length) return null;

  const inlineCount = Math.max(0, maxInline);
  const inline = visible.slice(0, inlineCount);
  const overflow = visible.slice(inlineCount);

  const renderBtn = (action) => (
    <AdsTableActionButton
      key={action.key}
      variant={action.danger || action.variant === 'danger' ? 'danger' : 'default'}
      title={action.label}
      aria-label={action.label}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        action.onClick?.(e);
      }}
    >
      {action.icon}
    </AdsTableActionButton>
  );

  return (
    <div
      ref={rootRef}
      className={`ads-row-actions ${className}`.trim()}
      onClick={(e) => e.stopPropagation()}
    >
      {inline.map(renderBtn)}
      {overflow.length > 0 && (
        <div className="ads-row-actions__overflow">
          <AdsTableActionButton
            title={moreLabel}
            aria-label={moreLabel}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={menuId}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            <MoreHorizontal size={16} aria-hidden />
          </AdsTableActionButton>
          {open && (
            <div id={menuId} className="ads-row-actions__menu" role="menu">
              {overflow.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  role="menuitem"
                  className={`ads-row-actions__menu-item${
                    action.danger || action.variant === 'danger'
                      ? ' ads-row-actions__menu-item--danger'
                      : ''
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(false);
                    action.onClick?.(e);
                  }}
                >
                  {action.icon ? (
                    <span className="ads-row-actions__menu-icon" aria-hidden>
                      {action.icon}
                    </span>
                  ) : null}
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
